
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useEmailProcessor } from "./email-processor/useEmailProcessor";
import { EnhancedEmailList } from "./email-processor/EnhancedEmailList";
import { fetchEnhancedProcessedEmailsWithFilters } from "./email-processor/enhancedEmailDataService";
import { EmailDirectionFilter } from "./EmailDirectionFilter";
import { useEmailAccounts } from "../hooks/useEmailAccounts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Mail, 
  RefreshCw, 
  Zap, 
  TrendingUp,
  Clock,
  BarChart3,
  Brain,
  Target,
  Inbox
} from "lucide-react";

export const UnifiedInboxView = () => {
  // Email accounts for multi-account support
  const { data: emailAccounts = [] } = useEmailAccounts();
  
  // Direction filter state
  const [emailDirection, setEmailDirection] = React.useState<'all' | 'incoming' | 'outgoing'>('all');

  // Use the enhanced data fetching with compatible filters
  const { data: enhancedEmailSummaries = [], isLoading: isFetchingEmails } = useQuery({
    queryKey: ['enhanced-processed-emails-unified', emailDirection],
    queryFn: () => fetchEnhancedProcessedEmailsWithFilters(supabase, {
      isArchived: false,
      isFlagged: false,
      direction: emailDirection,
    })
  });

  const {
    processEmailsMutation,
    archiveEmailMutation,
    flagEmailMutation,
    handleProcessEmails,
    handleArchiveEmail,
    handleFlagEmail,
  } = useEmailProcessor(5);

  // Calculate enhanced statistics
  const stats = React.useMemo(() => {
    const total = enhancedEmailSummaries.length;
    const incoming = enhancedEmailSummaries.filter(email => email.email_direction === 'incoming').length;
    const outgoing = enhancedEmailSummaries.filter(email => email.email_direction === 'outgoing').length;
    const flagged = enhancedEmailSummaries.filter(email => email.is_flagged).length;
    const highPriority = enhancedEmailSummaries.filter(email => email.priorityScore && email.priorityScore >= 7).length;
    const urgentResponse = enhancedEmailSummaries.filter(email => 
      email.responseUrgency === 'overdue' || email.responseUrgency === 'due_soon'
    ).length;
    const averagePriority = total > 0 ? 
      enhancedEmailSummaries
        .filter(email => email.priorityScore)
        .reduce((sum, email) => sum + (email.priorityScore || 0), 0) / 
      enhancedEmailSummaries.filter(email => email.priorityScore).length : 0;

    const sentimentBreakdown = enhancedEmailSummaries.reduce((acc, email) => {
      const sentiment = email.sentiment || 'unknown';
      acc[sentiment] = (acc[sentiment] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total,
      incoming,
      outgoing,
      flagged,
      highPriority,
      urgentResponse,
      averagePriority: Math.round(averagePriority * 10) / 10,
      sentimentBreakdown,
    };
  }, [enhancedEmailSummaries]);

  return (
    <div className="space-y-6">
      {/* Unified Header with Multi-Account Support */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Inbox className="h-6 w-6" />
            Unified Inbox
          </h2>
          <p className="text-muted-foreground mt-1">
            AI-powered email management across all your accounts
            {emailAccounts.length > 0 && (
              <span className="ml-2">
                • {emailAccounts.length} account{emailAccounts.length !== 1 ? 's' : ''} connected
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            onClick={handleProcessEmails}
            disabled={processEmailsMutation.isPending}
            className="flex items-center gap-2"
          >
            {processEmailsMutation.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Brain className="h-4 w-4" />
                Process New Emails
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Email Direction Filter */}
      <EmailDirectionFilter
        currentDirection={emailDirection}
        onDirectionChange={setEmailDirection}
        incomingCount={stats.incoming}
        outgoingCount={stats.outgoing}
        totalCount={stats.total}
      />

      {/* Enhanced Statistics Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-xs text-muted-foreground">Total Emails</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{stats.highPriority}</div>
            <div className="text-xs text-muted-foreground">High Priority</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.urgentResponse}</div>
            <div className="text-xs text-muted-foreground">Urgent Response</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.flagged}</div>
            <div className="text-xs text-muted-foreground">Flagged</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.averagePriority}</div>
            <div className="text-xs text-muted-foreground">Avg Priority</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {stats.sentimentBreakdown.positive || 0}
            </div>
            <div className="text-xs text-muted-foreground">Positive</div>
          </CardContent>
        </Card>
      </div>

      {/* Sentiment Breakdown */}
      {stats.total > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Sentiment Analysis Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.sentimentBreakdown).map(([sentiment, count]) => (
                <Badge 
                  key={sentiment} 
                  variant="outline" 
                  className={`
                    ${sentiment === 'positive' ? 'text-green-700 bg-green-50 border-green-200' : ''}
                    ${sentiment === 'negative' ? 'text-purple-700 bg-purple-50 border-purple-200' : ''}
                    ${sentiment === 'neutral' ? 'text-gray-700 bg-gray-50 border-gray-200' : ''}
                  `}
                >
                  {sentiment}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Unified Email List */}
      <EnhancedEmailList
        emailSummaries={enhancedEmailSummaries}
        isFetchingEmails={isFetchingEmails}
        isProcessing={processEmailsMutation.isPending}
        archiveEmailMutation={archiveEmailMutation}
        flagEmailMutation={flagEmailMutation}
        onArchiveEmail={handleArchiveEmail}
        onFlagEmail={handleFlagEmail}
        emailsToProcessPerRequest={5}
      />
    </div>
  );
};
